import type { LayerSpecification } from 'mapbox-gl';
import { CONSUMPTION_CLASSES, closeRing } from './domain';
import { PC } from './tokens';
import type { LngLat, PlanBuilding } from './types';

type FC = GeoJSON.FeatureCollection;
export const EMPTY: FC = { type: 'FeatureCollection', features: [] };

export function buildingsFC(buildings: PlanBuilding[], props: (b: PlanBuilding) => Record<string, number | string>): FC {
  return {
    type: 'FeatureCollection',
    features: buildings.map((b) => ({ type: 'Feature', id: b.num, properties: { id: b.num, fid: b.id, ...props(b) }, geometry: b.geometry })),
  };
}

export function ringFC(rings: LngLat[][]): FC {
  return {
    type: 'FeatureCollection',
    features: rings.filter((r) => r.length >= 3).map((r, i) => ({
      type: 'Feature', id: i + 1, properties: { id: i + 1 }, geometry: { type: 'Polygon', coordinates: [closeRing(r)] },
    })),
  };
}

/** Tracés des réseaux de chaleur sélectionnés. */
export function networksFC(networks: { id: string; name: string; path: LngLat[] }[]): FC {
  return {
    type: 'FeatureCollection',
    features: networks.filter((n) => n.path.length >= 2).map((n, i) => ({
      type: 'Feature', id: i + 1, properties: { id: n.id, name: n.name }, geometry: { type: 'LineString', coordinates: n.path },
    })),
  };
}

/** Polygone dessiné : surface + contour + sommets. */
export function drawFC(points: LngLat[], closed: boolean): FC {
  const features: GeoJSON.Feature[] = [];
  if (points.length >= 3) {
    features.push({ type: 'Feature', id: 1001, properties: { id: 1001 }, geometry: { type: 'Polygon', coordinates: [closeRing(points)] } });
    features.push({ type: 'Feature', id: 1000, properties: { id: 1000 }, geometry: { type: 'LineString', coordinates: closeRing(points) } });
  }
  // Sommets repères : uniquement une fois le polygone posé, et pas sur un tracé à main levée.
  if (closed && points.length >= 3 && points.length <= 12) {
    points.forEach((p, i) => features.push({ type: 'Feature', id: i + 1, properties: { id: i + 1, first: i === 0 ? 1 : 0 }, geometry: { type: 'Point', coordinates: p } }));
  }
  return { type: 'FeatureCollection', features };
}

/** Stations météo : zone d'influence (polygone) + point de mesure. */
export function stationsFC(
  stations: { id: string; name: string; position: LngLat; area: LngLat[] }[],
  selectedId: string | null,
): FC {
  const features: GeoJSON.Feature[] = [];
  stations.forEach((s, i) => {
    const sel = s.id === selectedId ? 1 : 0;
    features.push({
      type: 'Feature', id: 100 + i, properties: { id: s.id, name: s.name, sel },
      geometry: { type: 'Polygon', coordinates: [closeRing(s.area)] },
    });
    features.push({
      type: 'Feature', id: 200 + i, properties: { id: s.id, name: s.name, sel },
      geometry: { type: 'Point', coordinates: s.position },
    });
  });
  return { type: 'FeatureCollection', features };
}

/** Couches des stations météo, superposées à la carte de l'assistant. */
export function stationLayers(): LayerSpecification[] {
  return [
    { id: 'pc-station-zone', type: 'fill', source: 'stations', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': ['case', ['==', ['get', 'sel'], 1], PC.station, PC.stationIdle] as never, 'fill-opacity': ['case', ['==', ['get', 'sel'], 1], 0.14, 0.05] as never } },
    { id: 'pc-station-zone-line', type: 'line', source: 'stations', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'line-color': ['case', ['==', ['get', 'sel'], 1], PC.station, PC.stationIdle] as never, 'line-width': ['case', ['==', ['get', 'sel'], 1], 2, 1] as never, 'line-dasharray': [3, 2] } },
    { id: 'pc-station-point', type: 'circle', source: 'stations', filter: ['==', ['geometry-type'], 'Point'], paint: { 'circle-radius': ['case', ['==', ['get', 'sel'], 1], 9, 6] as never, 'circle-color': ['case', ['==', ['get', 'sel'], 1], PC.station, '#ffffff'] as never, 'circle-stroke-color': PC.station, 'circle-stroke-width': 2 } },
  ];
}

  /** Carte de l'assistant : bâtiments UM inclus / exclus, contours, réseaux, outil de dessin. */
export function perimeterLayers(): LayerSpecification[] {
  return [
    { id: 'pc-wiz-building', type: 'fill', source: 'buildings', paint: { 'fill-color': ['case', ['==', ['get', 'inc'], 1], PC.included, PC.excluded] as never, 'fill-opacity': ['case', ['==', ['get', 'inc'], 1], 0.75, 0.45] as never } },
    { id: 'pc-wiz-outline-fill', type: 'fill', source: 'outline', paint: { 'fill-color': PC.outline, 'fill-opacity': 0.06 } },
    { id: 'pc-wiz-outline', type: 'line', source: 'outline', paint: { 'line-color': PC.outline, 'line-width': 2, 'line-dasharray': [2, 1.5] } },
    { id: 'pc-wiz-network-halo', type: 'line', source: 'networks', paint: { 'line-color': '#ffffff', 'line-width': 7, 'line-opacity': 0.9 } },
    { id: 'pc-wiz-network', type: 'line', source: 'networks', paint: { 'line-color': PC.network, 'line-width': 3.5 } },
    { id: 'pc-draw-fill', type: 'fill', source: 'draw', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': PC.draw, 'fill-opacity': 0.14 } },
    { id: 'pc-draw-line', type: 'line', source: 'draw', filter: ['==', ['geometry-type'], 'LineString'], paint: { 'line-color': PC.draw, 'line-width': 2.5 } },
    {
      id: 'pc-draw-vertex', type: 'circle', source: 'draw', filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-radius': ['case', ['==', ['get', 'first'], 1], 6, 4] as never, 'circle-color': '#ffffff', 'circle-stroke-color': PC.draw, 'circle-stroke-width': 2 },
    },
  ];
}

const consoColor = (() => {
  const expr: unknown[] = ['step', ['get', 'conso'], CONSUMPTION_CLASSES[0].color];
  CONSUMPTION_CLASSES.slice(0, -1).forEach((c, i) => expr.push(c.max, CONSUMPTION_CLASSES[i + 1].color));
  return expr;
})();

/** Carte du projet : bâtiments colorés par conso corrigée, contour du périmètre, bâtiment sélectionné. */
export function projectLayers(pickedNum: number | null): LayerSpecification[] {
  return [
    { id: 'pc-prj-building', type: 'fill', source: 'buildings', paint: { 'fill-color': consoColor as never, 'fill-opacity': 0.9 } },
    { id: 'pc-prj-building-line', type: 'line', source: 'buildings', paint: { 'line-color': '#ffffff', 'line-width': 0.4, 'line-opacity': 0.6 } },
    { id: 'pc-prj-outline', type: 'line', source: 'outline', paint: { 'line-color': PC.outline, 'line-width': 2.5, 'line-dasharray': [2, 1.5] } },
    { id: 'pc-prj-picked', type: 'line', source: 'buildings', filter: ['==', ['get', 'id'], pickedNum ?? -1], paint: { 'line-color': '#6ED2B1', 'line-width': 3.5 } },
  ];
}
